import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import LogoutModal from '../components/common/LogoutModal';
import { useSelector, useDispatch } from 'react-redux';
import { BackIcon, MobileMenuIcon, StarrIcon } from '../assets/svgs/svgs';
import { fetchScript, shareScript, unshareScript } from '../store/actions/scriptsActions';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import { logoutUser } from '../store/actions/authActions';
import { parseScriptContentForDetails, extractImportantPointsFromJSON } from '../utils/formatMarkdown';

const ScriptDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { formData, topic, script, scriptId, scriptData: scriptDataFromState, fromPage } = location.state || {};

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [scriptData, setScriptData] = useState(null);
  const [shareToLibrary, setShareToLibrary] = useState(false);
  const [isTogglingShare, setIsTogglingShare] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const { currentScript } = useSelector((state) => state.scripts);

  useEffect(() => {
    if (script) {
      setScriptData(script);
      if (script.isShared !== undefined) {
        setShareToLibrary(script.isShared);
      }
    } else if (scriptDataFromState?.script) {
      setScriptData(scriptDataFromState.script);
      if (scriptDataFromState.script.isShared !== undefined) {
        setShareToLibrary(scriptDataFromState.script.isShared);
      }
    } else if (scriptId && !currentScript) {
      dispatch(fetchScript(scriptId)).then((data) => {
        if (data) {
          setScriptData(data);
          if (data.isShared !== undefined) {
            setShareToLibrary(data.isShared);
          }
        }
      });
    } else if (currentScript) {
      setScriptData(currentScript);
      if (currentScript.isShared !== undefined) {
        setShareToLibrary(currentScript.isShared);
      }
    }
  }, [script, scriptDataFromState, scriptId, currentScript, dispatch]);

  const isScript = scriptData?.content || scriptData?.topic;
  const isTopic = topic && !isScript;
  const isSharedScript = script && script.userId && script.userId._id !== user?._id;
  const scriptCreator = scriptData?.userId?.fullName || script?.userId?.fullName || script?.author || 'Unknown Creator';
  
  const getKeywordsArray = () => {
    const keywords = scriptData?.topic?.keywords || topic?.keywords;
    if (!keywords) return [];
    if (Array.isArray(keywords)) return keywords;
    return [
      keywords.main,
      ...(keywords.supporting || []),
      ...(keywords.autocomplete || [])
    ].filter(Boolean);
  };

  const getScriptContent = () => {
    const content = scriptData?.content || topic?.content;
    
    const defaultResult = {
      title: topic?.title || scriptData?.topic?.title || 'Untitled Script',
      introduction: '',
      introRollIn: '',
      whatIsCoreConcept: '',
      keyElements: '',
      settingsTools: [],
      extraTip: '',
      focusingStabilizing: [],
      compositionEnhancements: [],
      commonMistakes: [],
      editingRefinement: '',
      conclusion: '',
      cta: '',
      importantPoints: null
    };
    
    if (!content) return defaultResult;
    
    try {
      const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
      const contentObj = typeof content === 'string' ? JSON.parse(content) : content;
      const importantPoints = extractImportantPointsFromJSON(contentStr);
      
      if (contentObj.script) {
        const script = contentObj.script;
        return {
          title: script.title || scriptData?.Title || scriptData?.topic?.title || topic?.title || 'Untitled Script',
          introduction: script.introduction || '',
          introRollIn: script.introRollIn || '',
          whatIsCoreConcept: script.whatIsCoreConcept || '',
          keyElements: script.keyElements || '',
          settingsTools: Array.isArray(script.settingsTools) ? script.settingsTools : [],
          extraTip: script.extraTip || '',
          focusingStabilizing: Array.isArray(script.focusingStabilizing) ? script.focusingStabilizing : [],
          compositionEnhancements: Array.isArray(script.compositionEnhancements) ? script.compositionEnhancements : [],
          commonMistakes: Array.isArray(script.commonMistakes) ? script.commonMistakes : [],
          editingRefinement: script.editingRefinement || '',
          conclusion: script.conclusion || '',
          cta: script.callToAction || '',
          importantPoints: importantPoints
        };
      }
      
      return defaultResult;
    } catch (e) {
      return defaultResult;
    }
  };

  const scriptContent = getScriptContent();

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleShareToggle = async () => {
    const currentScriptId = scriptId || script?._id || script?.id || scriptData?._id || scriptData?.id;
    if (!currentScriptId || isTogglingShare) return;
    
    const currentShareState = shareToLibrary;
    const newShareState = !currentShareState;
    
    setShareToLibrary(newShareState);
    setIsTogglingShare(true);
    
    try {
      let success = false;
      if (newShareState) {
        success = await dispatch(shareScript(currentScriptId));
      } else {
        success = await dispatch(unshareScript(currentScriptId));
      }
      
      if (success === false) {
        setShareToLibrary(currentShareState);
      }
    } catch (error) {
      setShareToLibrary(currentShareState);
      console.error('Error toggling share:', error);
    } finally {
      setIsTogglingShare(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden bg-main">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage={fromPage || "my-scripts"}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-primary text-white py-4 px-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <MobileMenuIcon/>
              </button>

              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 border-2 border-white rounded-lg hover:bg-white/10 transition-colors"
              >
                <BackIcon/>
                <span className="font-medium text-sm">Back</span>
              </button>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-hidden p-6 flex flex-col">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-primary">
              {isTopic ? 'Topic Details' : (isSharedScript ? 'Script Details' : 'My Scripts')}
            </h1>
            {isSharedScript && scriptCreator && (
              <p className="text-sm text-secondary mt-2">Created by: <span className="font-semibold text-black">{scriptCreator}</span></p>
            )}
          </div>

          <div className="bg-white rounded-2xl flex-1 shadow-sm flex flex-col overflow-hidden py-6">
          <div className="overflow-y-auto flex-1 px-8 script-details-scroll">
            {!scriptData && isTopic && topic ? (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-black mb-2">
                  {topic?.title || 'Untitled Topic'}
                </h2>
                
                <p className="text-sm text-secondary leading-relaxed mb-4">
                  {topic?.description || 'No description available'}
                </p>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-black">Region & Category</span>
                    <div className="flex items-center text-sm font-semibold text-black">
                      <StarrIcon/>
                      {topic?.score || 0}/10 Score
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {topic?.region && (
                      <span className="tag-brown">{topic.region}</span>
                    )}
                    {topic?.category && (
                      <span className="tag-brown">{topic.category}</span>
                    )}
                  </div>
                </div>

                {getKeywordsArray().length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Keywords</h4>
                    <div className="flex gap-2 flex-wrap">
                      {getKeywordsArray().map((keyword, index) => (
                        <span key={index} className="tag-brown">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {topic?.createdBy && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Created By</h4>
                    <p className="text-sm text-secondary">{topic.createdBy.fullName || 'Unknown'}</p>
                  </div>
                )}

                {topic?.trend && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Trend</h4>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {topic.trend}
                    </span>
                  </div>
                )}

                {topic?.suggestedHook && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Suggested Hook</h4>
                    <p className="text-sm text-secondary leading-relaxed">{topic.suggestedHook}</p>
                  </div>
                )}

                {topic?.competitorVideos && Array.isArray(topic.competitorVideos) && topic.competitorVideos.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Competitor Videos</h4>
                    <div className="space-y-3">
                      {topic.competitorVideos.map((video, index) => (
                        <div key={video._id || index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="text-sm font-medium text-black">{video.creator || 'Unknown Creator'}</p>
                            {video.views && (
                              <span className="text-xs text-secondary whitespace-nowrap">
                                {typeof video.views === 'number' 
                                  ? video.views.toLocaleString() 
                                  : video.views} views
                              </span>
                            )}
                          </div>
                          {video.videoLink && (
                            <a 
                              href={video.videoLink} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline break-all"
                            >
                              {video.videoLink}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {topic?.geoTrend && Array.isArray(topic.geoTrend) && topic.geoTrend.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Geo Trend</h4>
                    <div className="flex gap-2 flex-wrap">
                      {topic.geoTrend.map((geo, index) => (
                        <div key={geo._id || index} className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                          <p className="text-xs font-medium text-black">{geo.country || 'Unknown'}</p>
                          {geo.rating !== undefined && geo.rating !== null && (
                            <div className="flex items-center gap-1 mt-1">
                              <StarrIcon className="w-3 h-3 text-yellow-500 stroke-current stroke-2" />
                              <span className="text-xs text-secondary">{geo.rating}/5</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {topic?.status && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-black mb-2">Status</h4>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      topic.status === 'available' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {topic.status === 'available' ? 'Available' : 'Claimed'}
                    </span>
                  </div>
                )}
              </div>
            ) : scriptData || script ? (
              <div className="space-y-5">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-black mb-2">
                    {scriptContent.title}
                  </h2>
                  {isSharedScript && scriptCreator && (
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-black mb-1">Created By</h4>
                      <p className="text-sm text-secondary">{scriptCreator}</p>
                    </div>
                  )}
                </div>
                
                {scriptContent.introduction && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Introduction</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.introduction}
                    </p>
                  </div>
                )}

                {scriptContent.introRollIn && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Intro Roll-In</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.introRollIn}
                    </p>
                  </div>
                )}

                {scriptContent.whatIsCoreConcept && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">What Is / Core Concept</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.whatIsCoreConcept}
                    </p>
                  </div>
                )}

                {scriptContent.keyElements && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Key Elements</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.keyElements}
                    </p>
                  </div>
                )}

                {scriptContent.settingsTools && scriptContent.settingsTools.length > 0 && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Settings & Tools</h3>
                    <ul className="space-y-2">
                      {scriptContent.settingsTools.map((item, idx) => (
                        <li key={idx} className="text-sm text-black leading-relaxed flex items-start gap-2">
                          <span className="text-gray-500">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {scriptContent.extraTip && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Extra Tip</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.extraTip}
                    </p>
                  </div>
                )}

                {scriptContent.focusingStabilizing && scriptContent.focusingStabilizing.length > 0 && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Focusing & Stabilizing</h3>
                    <ul className="space-y-2">
                      {scriptContent.focusingStabilizing.map((item, idx) => (
                        <li key={idx} className="text-sm text-black leading-relaxed flex items-start gap-2">
                          <span className="text-gray-500">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {scriptContent.compositionEnhancements && scriptContent.compositionEnhancements.length > 0 && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Composition Enhancements</h3>
                    <ul className="space-y-2">
                      {scriptContent.compositionEnhancements.map((item, idx) => (
                        <li key={idx} className="text-sm text-black leading-relaxed flex items-start gap-2">
                          <span className="text-gray-500">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {scriptContent.commonMistakes && scriptContent.commonMistakes.length > 0 && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Common Mistakes</h3>
                    <ul className="space-y-2">
                      {scriptContent.commonMistakes.map((item, idx) => (
                        <li key={idx} className="text-sm text-black leading-relaxed flex items-start gap-2">
                          <span className="text-gray-500">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {scriptContent.editingRefinement && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Editing & Refinement</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.editingRefinement}
                    </p>
                  </div>
                )}

                {scriptContent.conclusion && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Conclusion</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.conclusion}
                    </p>
                  </div>
                )}

                {scriptContent.cta && (
                  <div>
                    <h3 className="text-base font-bold text-black mb-2">Call to Action</h3>
                    <p className="text-sm text-black leading-relaxed whitespace-pre-line">
                      {scriptContent.cta}
                    </p>
                  </div>
                )}

                {scriptContent.importantPoints && (
                  <div className="border-t-2 border-gray-200 pt-5">
                    <h3 className="text-base font-bold text-black mb-4">Important Points</h3>
                    
                    {scriptContent.importantPoints.visualCuesAndSceneIdeas && scriptContent.importantPoints.visualCuesAndSceneIdeas.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Visual Cues and Scene Ideas</h4>
                        <ul className="space-y-1">
                          {scriptContent.importantPoints.visualCuesAndSceneIdeas.map((point, index) => (
                            <li key={index} className="text-sm text-black leading-relaxed flex items-start gap-2">
                              <span className="text-gray-500">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {scriptContent.importantPoints.stepByStepDemonstrations && scriptContent.importantPoints.stepByStepDemonstrations.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Step by Step Demonstrations</h4>
                        <ol className="space-y-1">
                          {scriptContent.importantPoints.stepByStepDemonstrations.map((point, index) => (
                            <li key={index} className="text-sm text-black leading-relaxed flex items-start gap-2">
                              <span className="text-gray-500 w-5">{index + 1}.</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {scriptContent.importantPoints.storytellingAndRelatableExamples && scriptContent.importantPoints.storytellingAndRelatableExamples.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Storytelling and Relatable Examples</h4>
                        <ul className="space-y-1">
                          {scriptContent.importantPoints.storytellingAndRelatableExamples.map((point, index) => (
                            <li key={index} className="text-sm text-black leading-relaxed flex items-start gap-2">
                              <span className="text-gray-500">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {scriptContent.importantPoints.engagementAndPacingEnhancers && scriptContent.importantPoints.engagementAndPacingEnhancers.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Engagement and Pacing Enhancers</h4>
                        <ul className="space-y-1">
                          {scriptContent.importantPoints.engagementAndPacingEnhancers.map((point, index) => (
                            <li key={index} className="text-sm text-black leading-relaxed flex items-start gap-2">
                              <span className="text-gray-500">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {scriptContent.importantPoints.additionalNotes && scriptContent.importantPoints.additionalNotes.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">Additional Notes</h4>
                        <ul className="space-y-1">
                          {scriptContent.importantPoints.additionalNotes.map((point, index) => (
                            <li key={index} className="text-sm text-black leading-relaxed flex items-start gap-2">
                              <span className="text-gray-500">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {!isSharedScript && (
                  <div className="rounded-2xl p-5 mt-6" style={{ 
                    backgroundColor: 'var(--color-primary-light)', 
                    border: '1px solid var(--color-primary-lighter)' 
                  }}>
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm text-gray-700">You want to share your script to the shared library?</p>
                      <button 
                        type="button"
                        onClick={handleShareToggle}
                        disabled={isTogglingShare || !(scriptId || script?._id || script?.id || scriptData?._id || scriptData?.id)}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                          shareToLibrary 
                            ? 'bg-primary' 
                            : 'bg-gray-300'
                        }`}
                      >
                        <span 
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-200 ease-in-out ${
                            shareToLibrary ? 'translate-x-6' : 'translate-x-1'
                          }`} 
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            )}
            <LogoutModal 
              showLogoutModal={showLogoutModal}
              onConfirm={confirmLogout}
              onCancel={cancelLogout}
            />
          </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ScriptDetails;